import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { normalizeVideoTagsForDisplay } from "../lib/videoTags";
import { videoCast, dedupeVideoCast, castSlug } from "../lib/videoCast";
import { getMemberUpgradePath } from "@/lib/memberRoutes";
import { useFeatureFlags } from "../lib/featureFlags";
import {
  FREE_VIDEOS_TAB, GALLERY_TAB, STUDIO_SCENES_TAB, CAST_TAB, STORE_TAB, ESCORT_TAB, DESIGN_SETTINGS_TAB, resolveFreeAreaTab, buildTabSearch, filterRegularVideos,
} from "@/lib/freeArea";
import FreeAreaTabs from "@/components/FreeAreaTabs";
import VideoThumbnail from "@/components/VideoThumbnail";
import GalleryMediaViewer from "@/components/GalleryMediaViewer";
import AdSlot from "../components/AdSlot";
import AdPlaceholder from "../components/FreeAreaAdPlaceholder";
import GalleryImage from "../components/GalleryImage";
import MessageCreatorWidget from "../components/MessageCreatorWidget";
import GlobalFooter from "../components/GlobalFooter";
import EscortCompanionTab from "../components/EscortCompanionTab";
import PostsFeed from "../components/PostsFeed";
import { toast } from "sonner";
import { 
  Play, Lock, Image as ImageIcon, Users, 
  Crown, ArrowRight, Sparkles, Eye, Star, LogOut, Home, ExternalLink, Building2,
  Menu, X, Palette, Settings, Video, Film, ChevronRight, ShoppingBag, Search,
  Heart, Flame, Zap, Target, Shield, Circle, MessageCircle, Clock, Calendar, UserCircle, MapPin, Phone
} from "lucide-react";

const PRODUCT_CATEGORIES = ["All", "Toys", "Lingerie", "Supplements", "Gear", "Travel", "Digital Products", "Affiliate Offers"];

// Sidebar tabs (DESIGN_SETTINGS_TAB now imported from freeArea.js)

export default function FreeMembersArea() {
  const { ENABLE_PAID_TIERS } = useFeatureFlags();
  const [videos, setVideos] = useState([]);
  const [studioScenes, setStudioScenes] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  const [selectedGalleryItem, setSelectedGalleryItem] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [productQuery, setProductQuery] = useState("");
  const [productCategory, setProductCategory] = useState("All");
  const [escortListings, setEscortListings] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [tierInfo, setTierInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  useEffect(()=>{if(sidebarOpen)document.getElementById("sidebar-video-search")?.focus();},[sidebarOpen]);
  const [memberAreaConfig, setMemberAreaConfig] = useState({
    title: "Welcome to the Collection",
    description: "Enjoy trailers, behind-the-scenes content, and studio highlights."
  });
  const [accentColor, setAccentColor] = useState("#E11D48");
  const [darkMode, setDarkMode] = useState(true);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = resolveFreeAreaTab(searchParams.get("tab"));
  const regularVideos = filterRegularVideos(videos, studioScenes);

  useEffect(() => {
    if (searchParams.get("tab") === "companion") {
      setSearchParams(new URLSearchParams(buildTabSearch(searchParams.toString(), ESCORT_TAB)), { replace: true });
    }
  }, [searchParams, setSearchParams]);
  useEffect(() => {
    document.title = activeTab === STORE_TAB ? "Shop & Affiliate | Gio Pilos" : "Free Members | Gio Pilos";
  }, [activeTab]);
  const castMembers = React.useMemo(
    () => dedupeVideoCast([...videos, ...studioScenes].flatMap(videoCast)),
    [videos, studioScenes]
  );

  // Video categories with icons and grouping
  const categoryGroups = [
    {
      name: "Popular",
      categories: [
        { id: "all", label: "All", icon: Circle },
        { id: "anal", label: "Anal", icon: Heart },
        { id: "oral", label: "Oral", icon: Heart },
        { id: "bareback", label: "Bareback", icon: Flame },
        { id: "groups", label: "Groups", icon: Users },
      ]
    },
    {
      name: "Body & Types",
      categories: [
        { id: "muscle", label: "Muscle", icon: Zap },
        { id: "bears", label: "Bears", icon: Shield },
        { id: "twinks", label: "Twinks", icon: Star },
        { id: "daddy", label: "Daddy", icon: Crown },
        { id: "jocks", label: "Jocks", icon: Target },
        { id: "body hair", label: "Body Hair" },
        { id: "tattoos", label: "Tattoos" },
      ]
    },
    {
      name: "Acts & Play",
      categories: [
        { id: "kissing", label: "Kissing" },
        { id: "rimming", label: "Rimming" },
        { id: "fisting", label: "Fisting" },
        { id: "deepthroat", label: "Deepthroat" },
        { id: "cum", label: "Cum" },
        { id: "role play", label: "Role Play" },
        { id: "spanking", label: "Spanking" },
        { id: "wrestling", label: "Wrestling" },
        { id: "edging", label: "Edging" },
        { id: "joi", label: "JOI" },
      ]
    },
    {
      name: "Fetish",
      categories: [
        { id: "feet", label: "Feet" },
        { id: "armpits", label: "Armpits" },
        { id: "nipples", label: "Nipples" },
        { id: "foreskin", label: "Foreskin" },
        { id: "socks", label: "Socks" },
        { id: "leather", label: "Leather" },
        { id: "latex", label: "Latex" },
        { id: "smoking", label: "Smoking" },
        { id: "ws", label: "WS" },
        { id: "pissing", label: "Pissing" },
        { id: "piercing", label: "Piercing" },
        { id: "shaving", label: "Shaving" },
        { id: "tickling", label: "Tickling" },
        { id: "worship", label: "Worship" },
        { id: "dirty", label: "Dirty" },
        { id: "verbal", label: "Verbal" },
        { id: "voyeur", label: "Voyeur" },
      ]
    },
    {
      name: "BDSM & Power",
      categories: [
        { id: "bondage", label: "Bondage" },
        { id: "domination", label: "Domination" },
        { id: "submission", label: "Submission" },
        { id: "s & m", label: "S & M" },
        { id: "toys", label: "Toys" },
        { id: "poppers", label: "Poppers" },
        { id: "pnp", label: "PNP" },
        { id: "fag", label: "Fag" },
      ]
    }
  ];

  const toggleCategory = (category) => {
    if (category === "all") {
      setSelectedCategories([]);
      return;
    }
    setSelectedCategories((previous) => previous.includes(category)
      ? previous.filter((item) => item !== category)
      : [...previous, category]);
  };

  const filteredVideos = React.useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return regularVideos.filter((video) => {
      const tags = normalizeVideoTagsForDisplay(video.tags).map((tag) => tag.toLowerCase());
      const matchesSearch = !query ||
        video.title?.toLowerCase().includes(query) ||
        video.description?.toLowerCase().includes(query) ||
        tags.some((tag) => tag.includes(query));
      const matchesCategories = selectedCategories.length === 0 ||
        selectedCategories.some((selected) => tags.includes(selected.toLowerCase()));
      return matchesSearch && matchesCategories;
    });
  }, [regularVideos, searchQuery, selectedCategories]);

  const changeTab = (tabId) =>
    setSearchParams(new URLSearchParams(buildTabSearch(searchParams.toString(), tabId)));

  const goToUpgrade = (plan = "basic") => navigate(getMemberUpgradePath(plan));
  const visibleProducts = products.filter((product) => {
    const query = productQuery.trim().toLowerCase();
    const matchesCategory = productCategory === "All"
      || (productCategory === "Affiliate Offers" ? Boolean(product.affiliate_url) : product.category?.toLowerCase() === productCategory.toLowerCase());
    const matchesSearch = !query || [product.title, product.merchant, product.description, product.category, ...(product.tags || [])]
      .some((value) => value?.toLowerCase().includes(query));
    return product.active === true && matchesCategory && matchesSearch;
  });

  useEffect(() => {
    loadTierContent();
    // Load member area config
    api.get("/api/landing/config")
      .then((res) => {
        if (res.data.free_area_title || res.data.free_area_description) {
          setMemberAreaConfig({
            title: res.data.free_area_title || "Welcome to the Collection",
            description: res.data.free_area_description || "Enjoy trailers, behind-the-scenes content, and studio highlights."
          });
        }
      })
      .catch(() => {}); // Use defaults
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTierContent = async () => {
    try {
      const [videosRes, tierRes, studioScenesRes] = await Promise.all([
        api.get("/api/member-content/tier/free/videos"),
        api.get("/api/member-content/my-tier"),
        api.get("/api/videos/studio-scenes")
      ]);

      setVideos(videosRes.data.videos || []);
      setTierInfo(tierRes.data);
      setStudioScenes(studioScenesRes.data || []);
      try {
        const galleryRes = await api.get("/api/member-content/gallery/free");
        setGalleryItems(galleryRes.data.items || []);
      } catch (galleryError) {
        console.error("Failed to load free gallery media:", galleryError);
        setGalleryItems([]);
      }
      try {
        const productsRes = await api.get("/api/products");
        setProducts(Array.isArray(productsRes.data) ? productsRes.data : []);
      } catch (err) {
        console.error("Failed to load shop products:", err);
        setProducts([]);
      }
      try {
        const escortRes = await api.get("/api/public/escort-profile", { _silent401: true });
        setEscortListings(escortRes.data || []);
      } catch (err) {
        console.error("Failed to load escort listings:", err);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error loading content:", error);
      toast.error("Failed to load content");
      setLoading(false);
    }
  };

  const trackStudioClick = async (video, studioName) => {
    try {
      await api.post("/api/videos/track-studio-click", {
        video_id: video.id,
        studio_name: studioName
      });
    } catch (e) {
      // Silent fail - don't block user
    }
  };

  const handleVideoClick = (video) => {
    // Free members can only view trailer + info
    navigate(`/video/${video.id}/preview`);
  };

  const handleLogout = async () => {
    try {
      await api.post("/api/auth/logout");
      navigate("/members/login");
    } catch (error) {
      toast.error("Logout failed");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#08090D] flex items-center justify-center">
        <div className="text-sm text-zinc-500 font-mono">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#08090D] text-white flex flex-col">
      {/* Sidebar */}
      <aside id="member-search-sidebar" className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#0F1118] border-r border-white/10 transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <div className="p-6 border-b border-white/10">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="relative">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#E11D48] to-[#FF10F0] flex items-center justify-center font-bold text-sm border-2 border-[#D4AF37]/50 shadow-[0_0_20px_rgba(225,29,72,0.3)]">
                  GP
                </div>
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-[#E11D48] rounded-full animate-pulse border-2 border-[#0F1118]" />
              </div>
              <div>
                <div className="text-lg font-extrabold tracking-tight" style={{ fontFamily: "'Syne', sans-serif" }}>
                  GioPilos
                </div>
                <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-[#D4AF37]">Free Tier</div>
              </div>
            </Link>
            <button
              aria-label="Close video search sidebar"
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden absolute top-4 right-4 p-2 hover:bg-white/5 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          <section role="search" aria-label="Search member videos" className="flex-1 p-4">
            <label htmlFor="sidebar-video-search" className="mb-3 block text-sm font-semibold text-white">Search videos</label>
            <div className="relative mb-4">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input id="sidebar-video-search" data-testid="sidebar-video-search" type="search"
                placeholder="Search videos by title, tags, or description..." value={searchQuery}
                onChange={(event)=>{setSearchQuery(event.target.value);if(activeTab!==FREE_VIDEOS_TAB)changeTab(FREE_VIDEOS_TAB);}}
                className="w-full rounded-xl border border-white/10 bg-[#0F1118] py-3 pl-12 pr-10 text-sm text-white placeholder-zinc-500 transition-colors focus:border-[#E11D48]/50 focus:outline-none" />
              {searchQuery && <button type="button" onClick={()=>setSearchQuery("")} aria-label="Clear video search" className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-2 text-zinc-400 hover:text-white"><X size={16}/></button>}
            </div>
            <p className="text-xs leading-6 text-zinc-400">Search the video collection here. Use the bottom bar to browse each section.</p>
          </section>

          <div className="px-4 pb-4">
            <AdSlot placement="sidebar" />
          </div>

          {/* Sidebar Footer */}
          <div className="p-4 pb-24 border-t border-white/10 space-y-2">
            <Link
              to="/"
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl font-semibold text-sm text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
              data-testid="sidebar-home"
            >
              <Home size={18} />
              Back to Home
            </Link>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl font-semibold text-sm text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
              data-testid="sidebar-logout"
            >
              <LogOut size={18} />
              Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content Area */}
      <div className="flex-1 pb-28 lg:ml-64">
        {/* Mobile Header */}
        <header className="lg:hidden sticky top-0 z-30 bg-[#08090D]/85 backdrop-blur-2xl border-b border-white/10">
          <div className="relative flex items-center justify-center px-4 py-4">
            <button type="button" aria-label="Open video search sidebar" aria-expanded={sidebarOpen} aria-controls="member-search-sidebar" onClick={()=>setSidebarOpen(true)} className="absolute left-4 top-1/2 -translate-y-1/2 rounded-lg p-2 text-zinc-300 hover:bg-white/5 hover:text-white"><Search size={20}/></button>
            <div className="text-lg font-extrabold tracking-tight" style={{ fontFamily: "'Syne', sans-serif" }}>
              GioPilos
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 py-8 w-full">
        {/* Free Tier Banner */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0F1118] via-[#151822] to-[#08090D] border border-white/10 p-6 md:p-8 shadow-2xl mb-8">
          {/* Ambient Glow Backgrounds */}
          <div className="absolute top-0 left-0 w-64 h-64 bg-[#E11D48] opacity-10 blur-[100px] rounded-full" />
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-[#D4AF37] opacity-10 blur-[100px] rounded-full" />
          
          <div className="relative z-10">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-[#E11D48]/20 to-[#FF10F0]/20 border border-[#E11D48]/30 rounded-full text-xs font-bold uppercase tracking-wider text-[#FF10F0] mb-4">
                  <Sparkles size={12} />
                  Free Tier Access
                </div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight mb-2" style={{ fontFamily: "'Syne', sans-serif" }}>
                  {memberAreaConfig.title}
                </h1>
                <p className="text-sm sm:text-base text-zinc-400 max-w-2xl">
                  {memberAreaConfig.description}
                </p>
              </div>
              {ENABLE_PAID_TIERS && (
                <button
                  onClick={() => goToUpgrade("vip")}
                  className="px-6 py-3 bg-gradient-to-r from-[#E11D48] to-[#FF10F0] text-white font-bold tracking-wider rounded-full shadow-[0_0_25px_rgba(225,29,72,0.4)] hover:shadow-[0_0_35px_rgba(255,16,240,0.5)] hover:scale-105 active:scale-95 transition-all duration-200 text-sm flex items-center gap-2"
                  data-testid="upgrade-to-vip-button"
                >
                  <Crown size={18} />
                  Join VIP Access
                  <ArrowRight size={18} />
                </button>
              )}
            </div>
          </div>
        </div>

        <AdSlot placement="free_members_main" className="mb-8" />

        {/* Content Area */}
        {activeTab === DESIGN_SETTINGS_TAB && (
          <div>
            <div className="mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: "'Syne', sans-serif" }}>
                Design Settings
              </h2>
              <p className="text-sm text-zinc-500">Customize your viewing experience</p>
            </div>

            <div className="space-y-6">
              {/* Accent Color Picker */}
              <div className="bg-[#0F1118] border border-white/10 rounded-2xl p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1">Accent Color</h3>
                    <p className="text-sm text-zinc-500">Choose your preferred theme color</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl border-2 border-white/20" style={{ backgroundColor: accentColor }} />
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { name: "Rose", color: "#E11D48" },
                    { name: "Neon Pink", color: "#FF10F0" },
                    { name: "Gold", color: "#D4AF37" },
                    { name: "Purple", color: "#A855F7" },
                    { name: "Blue", color: "#3B82F6" },
                    { name: "Emerald", color: "#10B981" },
                    { name: "Orange", color: "#F59E0B" },
                    { name: "Cyan", color: "#06B6D4" },
                  ].map((colorOption) => (
                    <button
                      key={colorOption.color}
                      onClick={() => {
                        setAccentColor(colorOption.color);
                        toast.success(`Accent color changed to ${colorOption.name}`);
                      }}
                      data-testid={`accent-color-${colorOption.name.toLowerCase()}`}
                      className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all duration-200 ${
                        accentColor === colorOption.color
                          ? "border-white/40 bg-white/5"
                          : "border-white/10 hover:border-white/20 hover:bg-white/5"
                      }`}
                    >
                      <div
                        className="w-10 h-10 rounded-lg"
                        style={{ backgroundColor: colorOption.color }}
                      />
                      <span className="text-xs font-semibold text-zinc-400">{colorOption.name}</span>
                      {accentColor === colorOption.color && (
                        <div className="text-[10px] text-white font-bold">✓ Active</div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Display Mode */}
              <div className="bg-[#0F1118] border border-white/10 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-4">Display Mode</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <button
                    onClick={() => {
                      setDarkMode(true);
                      toast.success("Dark mode enabled");
                    }}
                    data-testid="display-mode-dark"
                    className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all duration-200 ${
                      darkMode
                        ? "border-[#E11D48] bg-[#E11D48]/10"
                        : "border-white/10 hover:border-white/20"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#08090D] to-[#0F1118] border border-white/10 flex items-center justify-center">
                        <div className="w-6 h-6 rounded bg-[#E11D48]" />
                      </div>
                      <div className="text-left">
                        <div className="font-bold text-white">Dark Mode</div>
                        <div className="text-xs text-zinc-500">Perfect for night viewing</div>
                      </div>
                    </div>
                    {darkMode && <div className="text-[#E11D48] font-bold">✓</div>}
                  </button>

                  <button
                    onClick={() => {
                      setDarkMode(false);
                      toast.info("Light mode coming soon!");
                    }}
                    data-testid="display-mode-light"
                    className="flex items-center justify-between p-4 rounded-xl border-2 border-white/10 hover:border-white/20 transition-all duration-200 opacity-50 cursor-not-allowed"
                    disabled
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-white to-zinc-100 border border-zinc-300 flex items-center justify-center">
                        <div className="w-6 h-6 rounded bg-[#E11D48]" />
                      </div>
                      <div className="text-left">
                        <div className="font-bold text-white">Light Mode</div>
                        <div className="text-xs text-zinc-500">Coming soon</div>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Video Preferences */}
              <div className="bg-[#0F1118] border border-white/10 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-4">Video Preferences</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-white">Autoplay Videos</div>
                      <div className="text-xs text-zinc-500 mt-0.5">Automatically play next video</div>
                    </div>
                    <button
                      className="relative w-12 h-6 bg-white/10 rounded-full transition-colors hover:bg-white/20"
                      data-testid="autoplay-toggle"
                    >
                      <div className="absolute left-1 top-1 w-4 h-4 bg-zinc-600 rounded-full transition-transform" />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-white">Show Thumbnails on Hover</div>
                      <div className="text-xs text-zinc-500 mt-0.5">Preview clips when hovering over videos</div>
                    </div>
                    <button
                      className="relative w-12 h-6 bg-[#E11D48] rounded-full"
                      data-testid="hover-thumbnails-toggle"
                    >
                      <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full" />
                    </button>
                  </div>
                </div>
              </div>

              {ENABLE_PAID_TIERS && <div className="bg-[#0F1118] border border-white/10 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-white">Video Player Theme</h3>
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-[#D4AF37]/20 to-[#F59E0B]/20 border border-[#D4AF37]/40 rounded-full">
                    <Crown size={14} className="text-[#D4AF37]" />
                    <span className="text-xs font-bold text-[#D4AF37]">VIP</span>
                  </div>
                </div>
                
                <div className="space-y-3">
                  {/* Default Theme */}
                  <button
                    onClick={() => toast.info("Theme applied!")}
                    data-testid="player-theme-default"
                    className="w-full flex items-center justify-between p-4 rounded-xl border-2 border-[#E11D48] bg-[#E11D48]/10 transition-all duration-200"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#E11D48] to-[#BE123C] flex items-center justify-center">
                        <Play size={20} className="text-white" />
                      </div>
                      <div className="text-left">
                        <div className="font-bold text-white">Dark Luxury</div>
                        <div className="text-xs text-zinc-500">Current theme</div>
                      </div>
                    </div>
                    <div className="text-[#E11D48] font-bold">✓</div>
                  </button>

                  {/* VIP Themes - Locked */}
                  <button
                    onClick={() => toast.info("Upgrade to VIP to unlock advanced themes!")}
                    data-testid="player-theme-cinema"
                    className="w-full flex items-center justify-between p-4 rounded-xl border-2 border-white/10 hover:border-[#D4AF37]/30 transition-all duration-200 opacity-60 cursor-not-allowed"
                    disabled
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center relative">
                        <Play size={20} className="text-zinc-400" />
                        <Lock size={12} className="absolute -top-1 -right-1 text-[#D4AF37]" />
                      </div>
                      <div className="text-left">
                        <div className="font-bold text-white">Cinema Mode</div>
                        <div className="text-xs text-zinc-500">Ambient lighting & focus</div>
                      </div>
                    </div>
                    <Crown size={16} className="text-[#D4AF37]" />
                  </button>

                  <button
                    onClick={() => toast.info("Upgrade to VIP to unlock advanced themes!")}
                    data-testid="player-theme-minimal"
                    className="w-full flex items-center justify-between p-4 rounded-xl border-2 border-white/10 hover:border-[#D4AF37]/30 transition-all duration-200 opacity-60 cursor-not-allowed"
                    disabled
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-white/5 to-white/10 flex items-center justify-center relative">
                        <Play size={20} className="text-white" />
                        <Lock size={12} className="absolute -top-1 -right-1 text-[#D4AF37]" />
                      </div>
                      <div className="text-left">
                        <div className="font-bold text-white">Minimal</div>
                        <div className="text-xs text-zinc-500">Clean & distraction-free</div>
                      </div>
                    </div>
                    <Crown size={16} className="text-[#D4AF37]" />
                  </button>
                </div>
              </div>}

              {ENABLE_PAID_TIERS && <div className="bg-[#0F1118] border border-white/10 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-white">Layout Density</h3>
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-[#D4AF37]/20 to-[#F59E0B]/20 border border-[#D4AF37]/40 rounded-full">
                    <Crown size={14} className="text-[#D4AF37]" />
                    <span className="text-xs font-bold text-[#D4AF37]">VIP</span>
                  </div>
                </div>
                
                <div className="space-y-3">
                  {/* Comfortable - Active */}
                  <button
                    onClick={() => toast.info("Layout applied!")}
                    data-testid="layout-density-comfortable"
                    className="w-full flex items-center justify-between p-4 rounded-xl border-2 border-[#E11D48] bg-[#E11D48]/10 transition-all duration-200"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-[#0F1118] border border-white/10 flex flex-col gap-1 items-center justify-center p-1.5">
                        <div className="w-full h-1 bg-[#E11D48] rounded" />
                        <div className="w-full h-1 bg-[#E11D48] rounded" />
                        <div className="w-full h-1 bg-[#E11D48] rounded" />
                      </div>
                      <div className="text-left">
                        <div className="font-bold text-white">Comfortable</div>
                        <div className="text-xs text-zinc-500">More spacing, easier to scan</div>
                      </div>
                    </div>
                    <div className="text-[#E11D48] font-bold">✓</div>
                  </button>

                  {/* Compact - VIP Locked */}
                  <button
                    onClick={() => toast.info("Upgrade to VIP to unlock compact layout!")}
                    data-testid="layout-density-compact"
                    className="w-full flex items-center justify-between p-4 rounded-xl border-2 border-white/10 hover:border-[#D4AF37]/30 transition-all duration-200 opacity-60 cursor-not-allowed"
                    disabled
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-[#0F1118] border border-white/10 flex flex-col gap-0.5 items-center justify-center p-1.5 relative">
                        <div className="w-full h-1 bg-zinc-600 rounded" />
                        <div className="w-full h-1 bg-zinc-600 rounded" />
                        <div className="w-full h-1 bg-zinc-600 rounded" />
                        <div className="w-full h-1 bg-zinc-600 rounded" />
                        <Lock size={12} className="absolute -top-1 -right-1 text-[#D4AF37]" />
                      </div>
                      <div className="text-left">
                        <div className="font-bold text-white">Compact</div>
                        <div className="text-xs text-zinc-500">More content per screen</div>
                      </div>
                    </div>
                    <Crown size={16} className="text-[#D4AF37]" />
                  </button>

                  {/* Spacious - VIP Locked */}
                  <button
                    onClick={() => toast.info("Upgrade to VIP to unlock spacious layout!")}
                    data-testid="layout-density-spacious"
                    className="w-full flex items-center justify-between p-4 rounded-xl border-2 border-white/10 hover:border-[#D4AF37]/30 transition-all duration-200 opacity-60 cursor-not-allowed"
                    disabled
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-[#0F1118] border border-white/10 flex flex-col gap-2 items-center justify-center p-1.5 relative">
                        <div className="w-full h-1 bg-zinc-600 rounded" />
                        <div className="w-full h-1 bg-zinc-600 rounded" />
                        <Lock size={12} className="absolute -top-1 -right-1 text-[#D4AF37]" />
                      </div>
                      <div className="text-left">
                        <div className="font-bold text-white">Spacious</div>
                        <div className="text-xs text-zinc-500">Maximum breathing room</div>
                      </div>
                    </div>
                    <Crown size={16} className="text-[#D4AF37]" />
                  </button>
                </div>
              </div>}

              {/* Info Box */}
              {ENABLE_PAID_TIERS && (
              <div className="rounded-2xl bg-gradient-to-r from-[#E11D48]/15 via-[#151822] to-[#D4AF37]/15 border border-[#D4AF37]/40 p-6 backdrop-blur-xl">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#F59E0B] flex items-center justify-center">
                      <Crown size={24} className="text-black" />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-white mb-1">Upgrade for More Customization</h4>
                    <p className="text-sm text-zinc-400 mb-3">
                      VIP members get access to advanced themes, custom layouts, and exclusive design presets.
                    </p>
                    {ENABLE_PAID_TIERS && (
                      <button
                        onClick={() => goToUpgrade("vip")}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#E11D48] to-[#FF10F0] text-white font-bold text-sm rounded-full hover:shadow-[0_0_20px_rgba(225,29,72,0.4)] transition-all"
                        data-testid="design-settings-upgrade-button"
                      >
                        <Crown size={16} />
                        Upgrade to VIP
                        <ArrowRight size={16} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
              )}
            </div>
          </div>
        )}

        {activeTab === FREE_VIDEOS_TAB && (
          <div>
            {/* Header with Search */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold tracking-tight" style={{ fontFamily: "'Syne', sans-serif" }}>
                    Free Videos & Trailers
                  </h2>
                  <p className="text-sm text-zinc-500 mt-1">{filteredVideos.length} of {regularVideos.length} videos</p>
                </div>
              </div>

              {/* Category Groups - Compact */}
              <div className="space-y-3">
                {categoryGroups.map((group) => (
                  <div key={group.name}>
                    <h3 className="text-xs font-bold text-zinc-600 uppercase tracking-wider mb-1.5 px-1">
                      {group.name}
                    </h3>
                    <div className="flex flex-wrap gap-1.5">
                      {group.categories.map((category) => {
                        const Icon = category.icon;
                        const isActive = category.id === "all"
                          ? selectedCategories.length === 0
                          : selectedCategories.includes(category.id);
                        return (
                          <button
                            key={category.id}
                            onClick={() => toggleCategory(category.id)}
                            aria-pressed={isActive}
                            className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-all flex items-center gap-1 ${
                              isActive
                                ? "bg-gradient-to-r from-[#E11D48] to-[#FF10F0] text-white shadow-[0_0_15px_rgba(225,29,72,0.4)]"
                                : "bg-[#0F1118] text-zinc-400 hover:text-white hover:bg-white/5 border border-white/10 hover:border-white/20"
                            }`}
                          >
                            {Icon && <Icon size={12} className={isActive ? "text-white" : "text-zinc-500"} />}
                            {category.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {filteredVideos.length === 0 ? (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#0F1118] border border-white/10 mb-4">
                  <Play size={24} className="text-zinc-600" />
                </div>
                <p className="text-zinc-500">No videos match your filters.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredVideos.map((video, index) => (
                  <React.Fragment key={video.id}>
                    <div
                      className="group bg-[#0F1118] border border-white/10 rounded-2xl overflow-hidden hover:border-[#E11D48]/50 transition-all duration-300 shadow-lg hover:-translate-y-1 cursor-pointer"
                      onClick={() => handleVideoClick(video)}
                      data-testid={`video-card-${video.id}`}
                    >
                      <VideoThumbnail video={video} freeAreaMode={true} />
                      <div className="p-4">
                        <h3 className="font-bold text-white mb-2 line-clamp-2 text-sm group-hover:text-[#E11D48] transition-colors">{video.title}</h3>
                        {video.tags && video.tags.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2">{normalizeVideoTagsForDisplay(video.tags).slice(0, 3).map((tag, idx) => <span key={idx} className="px-2 py-0.5 bg-[#151822] border border-white/10 rounded-full text-[10px] text-zinc-400">{tag}</span>)}</div>}
                      </div>
                    </div>
                    <AdSlot placement="below_every_video" slotIndex={index} className="col-span-full" />
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === STUDIO_SCENES_TAB && (
          <div>
            <div className="mb-6">
              <h2 className="text-xl sm:text-2xl font-bold tracking-tight mb-2" style={{ fontFamily: "'Syne', sans-serif" }}>
                Studio Scenes
              </h2>
              <p className="text-sm text-zinc-500">Behind-the-scenes and studio productions</p>
            </div>

            {studioScenes.length === 0 ? (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#0F1118] border border-white/10 mb-4">
                  <Building2 size={24} className="text-zinc-600" />
                </div>
                <p className="text-zinc-500">No studio scenes available yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {studioScenes.map((video, index) => (
                  <React.Fragment key={video.id}>
                    <div
                      className="group bg-[#0F1118] border border-white/10 rounded-2xl overflow-hidden hover:border-[#E11D48]/50 transition-all duration-300 shadow-lg hover:-translate-y-1 cursor-pointer"
                      onClick={() => {
                        if (video.studio_crm_id) trackStudioClick(video, video.studio_crm_id);
                        handleVideoClick(video);
                      }}
                      data-testid={`studio-scene-card-${video.id}`}
                    >
                      <VideoThumbnail video={video} freeAreaMode={true} />
                      <div className="p-4">
                        {video.studio_crm_id && <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gradient-to-r from-[#D4AF37]/20 to-[#F59E0B]/10 border border-[#D4AF37]/30 rounded-full text-[10px] font-bold text-[#D4AF37] mb-2"><Building2 size={10} />Studio</div>}
                        <h3 className="font-bold text-white mb-2 line-clamp-2 text-sm group-hover:text-[#E11D48] transition-colors">{video.title}</h3>
                      </div>
                    </div>
                    <AdSlot placement="below_every_video" slotIndex={index} className="col-span-full" />
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === GALLERY_TAB && (
          <div>
            {/* Posts & Updates Feed */}
            <PostsFeed />

            {/* Original Gallery */}
            <div className="mb-6">
              <h2 className="text-xl sm:text-2xl font-bold tracking-tight mb-2" style={{ fontFamily: "'Syne', sans-serif" }}>
                Media Gallery
              </h2>
              <p className="text-sm text-zinc-500">Photos and media from the studio</p>
            </div>

            {galleryItems.length === 0 ? (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#0F1118] border border-white/10 mb-4">
                  <MessageCircle size={24} className="text-zinc-600" />
                </div>
                <p className="text-zinc-500">No posts available yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {galleryItems.map((item, idx) => (
                  <div
                    key={item.id || idx}
                    data-testid={`post-card-${idx}`}
                    className="bg-[#0F1118] border border-white/10 rounded-2xl overflow-hidden hover:border-[#E11D48]/30 transition-all group"
                  >
                    {/* Post Image/Media */}
                    {item.url && (
                      <div 
                        className="relative aspect-[4/3] overflow-hidden cursor-pointer bg-black"
                        onClick={() => setSelectedGalleryItem(item)}
                      >
                        <img
                          src={item.url}
                          alt={item.caption || `Post ${idx + 1}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="absolute bottom-4 left-4 flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
                              <Eye size={16} className="text-white" />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Post Content */}
                    <div className="p-4 space-y-3">
                      {/* Post Caption/Text */}
                      {item.caption && (
                        <p className="text-white text-sm leading-relaxed line-clamp-3">
                          {item.caption}
                        </p>
                      )}

                      {/* Post Metadata */}
                      <div className="flex items-center justify-between text-xs text-zinc-500 pt-2 border-t border-white/5">
                        <div className="flex items-center gap-4">
                          {item.created_at && (
                            <div className="flex items-center gap-1.5">
                              <Calendar size={12} />
                              <span>{new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                            </div>
                          )}
                          {item.category && (
                            <span className="px-2 py-0.5 bg-[#E11D48]/20 text-[#E11D48] rounded-full text-xs font-semibold">
                              {item.category}
                            </span>
                          )}
                        </div>
                        {item.likes !== undefined && (
                          <div className="flex items-center gap-1.5 text-zinc-400">
                            <Heart size={12} />
                            <span>{item.likes || 0}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === CAST_TAB && (
          <div>
            <div className="mb-6">
              <h2 className="text-xl sm:text-2xl font-bold tracking-tight mb-2" style={{ fontFamily: "'Syne', sans-serif" }}>
                Cast & Crew
              </h2>
              <p className="text-sm text-zinc-500">Meet the talent behind the scenes</p>
            </div>

            <AdSlot placement="cast" className="mb-6" />

            {castMembers.length === 0 ? (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#0F1118] border border-white/10 mb-4">
                  <Users size={24} className="text-zinc-600" />
                </div>
                <p className="text-zinc-500">No cast members listed yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {castMembers.map((member) => (
                  <Link
                    key={member.id || member.name}
                    to={`/cast/${castSlug(member)}`}
                    className="group rounded-2xl overflow-hidden bg-[#0F1118] border border-white/10 hover:border-[#E11D48]/40 transition-all"
                    data-testid={`cast-member-${member.id || member.name}`}
                  >
                    {member.photo ? (
                      <img
                        src={member.photo}
                        alt={member.name}
                        className="w-full aspect-[3/4] object-cover"
                      />
                    ) : (
                      <img src="/placeholder-model.jpg" alt={member.name || "Cast member"} className="w-full aspect-[3/4] object-cover" />
                    )}
                    <div className="p-4">
                      <h3 className="font-semibold text-white">
                        {member.name}
                      </h3>
                      {member.role && (
                        <p className="text-xs text-zinc-500 mt-1">{member.role}</p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Shop & Affiliate Tab */}
        {activeTab === STORE_TAB && (
          <div>
            <div className="mb-6">
              <h2 className="text-xl sm:text-2xl font-bold tracking-tight mb-2" style={{ fontFamily: "'Syne', sans-serif" }}>
                Shop &amp; Affiliate
              </h2>
              <p className="text-sm text-zinc-500">Curated products, recommendations and affiliate offers.</p>
            </div>
            <AdSlot placement="shop_affiliate" className="mb-6" />
            <div className="relative mb-4">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
              <input type="search" value={productQuery} onChange={(event) => setProductQuery(event.target.value)} placeholder="Search products by name, brand, tag or description…" aria-label="Search products" className="w-full rounded-xl border border-white/10 bg-[#0F1118] py-3 pl-12 pr-4 text-white placeholder:text-zinc-500 focus:border-[#E11D48]/50 focus:outline-none" />
            </div>
            <div className="mb-5 flex flex-wrap gap-2" role="group" aria-label="Filter products by category">
              {PRODUCT_CATEGORIES.map((category) => <button key={category} type="button" aria-pressed={productCategory === category} onClick={() => setProductCategory(category)} className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${productCategory === category ? "border-transparent bg-gradient-to-r from-[#E11D48] to-[#FF10F0] text-white" : "border-white/10 bg-[#0F1118] text-zinc-400 hover:text-white"}`}>{category}</button>)}
            </div>
            <p className="mb-5 text-xs leading-5 text-zinc-500">Some links on this page may be affiliate links. Gio may earn a commission at no additional cost to you.</p>
            {products.length === 0 ? (
              <div className="rounded-2xl border border-white/10 bg-[#0F1118] px-5 py-16 text-center">
                <h3 className="text-lg font-semibold text-white">Curated recommendations coming soon</h3>
                <p className="mt-2 text-sm text-zinc-400">New products and affiliate recommendations will appear here. Check back soon.</p>
              </div>
            ) : visibleProducts.length === 0 ? (
              <div className="rounded-2xl border border-white/10 bg-[#0F1118] px-5 py-14 text-center text-sm text-zinc-400">No products match your filters.</div>
            ) : (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {visibleProducts.map((product) => (
                  <article key={product.id} className="group overflow-hidden rounded-2xl border border-white/10 bg-[#0F1118] transition hover:-translate-y-1 hover:border-[#E11D48]/50" data-testid={`shop-product-${product.id}`}>
                    <div className="relative aspect-[4/3] overflow-hidden bg-[#090A0F]">
                      {product.image_url ? <img src={product.image_url} alt={product.title} loading="lazy" className="h-full w-full object-cover transition duration-300 group-hover:scale-105" /> : <div className="flex h-full items-center justify-center text-zinc-600"><ShoppingBag size={42} /></div>}
                      {product.featured && <span className="absolute left-3 top-3 rounded-full bg-[#D4AF37] px-2.5 py-1 text-[10px] font-bold uppercase text-black">Featured</span>}
                    </div>
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-2"><h3 className="font-semibold text-white">{product.title}</h3>{product.category && <span className="shrink-0 rounded-full border border-white/10 px-2 py-1 text-[10px] text-zinc-400">{product.category}</span>}</div>
                      {product.merchant && <p className="mt-1 text-xs text-zinc-500">{product.merchant}</p>}
                      {product.description && <p className="mt-3 line-clamp-3 text-sm leading-5 text-zinc-400">{product.description}</p>}
                      {product.price != null && <p className="mt-3 font-semibold text-rose-200">{new Intl.NumberFormat("en-IE", { style: "currency", currency: product.currency || "EUR" }).format(product.price)}</p>}
                      {(product.affiliate_url || product.commission_note) && <p className="mt-2 text-[11px] text-zinc-500">{product.commission_note || "Affiliate link"}</p>}
                      <a href={product.affiliate_url || "#"} target="_blank" rel="noopener noreferrer sponsored" className="mt-4 flex min-h-10 items-center justify-between rounded-lg bg-white/5 px-3 text-sm font-semibold text-white transition hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#FF10F0]">
                        {product.affiliate_url ? "Shop Now" : "View Product"}<ExternalLink size={15} />
                      </a>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === ESCORT_TAB && <EscortCompanionTab listings={escortListings} />}

        {/* Upgrade CTA Box */}
        {ENABLE_PAID_TIERS && (
          <div className="mt-12 rounded-2xl bg-gradient-to-r from-[#E11D48]/15 via-[#151822] to-[#D4AF37]/15 border border-[#D4AF37]/40 p-8 text-center backdrop-blur-xl relative overflow-hidden shadow-2xl">
            {/* Ambient Glow */}
            <div className="absolute top-0 left-1/4 w-48 h-48 bg-[#E11D48] opacity-20 blur-[80px] rounded-full" />
            <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-[#D4AF37] opacity-20 blur-[80px] rounded-full" />
            
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] text-black font-extrabold text-xs rounded-full shadow-[0_0_12px_rgba(212,175,55,0.3)] mb-4">
                <Crown size={12} />
                VIP EXCLUSIVE
              </div>
              <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-3 bg-gradient-to-r from-[#D4AF37] via-[#F59E0B] to-[#D4AF37] bg-clip-text text-transparent" style={{ fontFamily: "'Syne', sans-serif" }}>
                Unlock Full Uncut Experience
              </h2>
              <p className="text-zinc-400 text-sm sm:text-base max-w-2xl mx-auto mb-6">
                Get instant access to all full-length videos, exclusive content, and premium features. No ads, no limits.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <button
                  onClick={() => goToUpgrade("vip")}
                  className="px-8 py-4 bg-gradient-to-r from-[#E11D48] via-[#FF10F0] to-[#E11D48] text-white font-bold tracking-wider rounded-full shadow-[0_0_25px_rgba(225,29,72,0.4)] hover:shadow-[0_0_35px_rgba(255,16,240,0.5)] hover:scale-105 active:scale-95 transition-all duration-200 text-sm flex items-center gap-2"
                  data-testid="upgrade-cta-vip"
                >
                  <Crown size={18} />
                  Upgrade to VIP
                  <ArrowRight size={18} />
                </button>
                <button
                  onClick={() => goToUpgrade("basic")}
                  className="px-8 py-4 bg-transparent border-2 border-[#D4AF37] text-[#D4AF37] font-bold tracking-wider rounded-full hover:bg-[#D4AF37]/10 hover:scale-105 active:scale-95 transition-all duration-200 text-sm flex items-center gap-2"
                  data-testid="upgrade-cta-basic"
                >
                  <Star size={18} />
                  View Plans
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Message Creator Widget */}
        <div className="mt-12">
          <MessageCreatorWidget />
        </div>
      </main>

      {/* Ad Placement Before Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
        <AdPlaceholder />
      </div>

      {/* Global Footer - Pinned to Bottom */}
      <GlobalFooter />
      <AdSlot placement="below_footer" />
      
      {/* Bottom Navigation Bar - Fixed */}
      <div className="fixed bottom-[76px] left-0 right-0 z-40 px-2 sm:hidden">
        <AdSlot placement="mobile_bottom_banner" />
      </div>
      <nav aria-label="Member sections" className="fixed bottom-0 left-0 right-0 z-40 bg-[#0F1118] border-t border-white/20 safe-area-pb">
        <div className="mx-auto grid max-w-4xl grid-cols-7 items-center gap-1 px-1 py-2 sm:gap-2 sm:px-3">
          {[
            { id: FREE_VIDEOS_TAB, label: "Videos", mobileLabel:"Videos", icon: Play, testid:"free-videos-tab" },
            { id: STUDIO_SCENES_TAB, label:"Studio Scenes", mobileLabel:"Studios", icon:Building2, testid:"studio-scenes-tab" },
            { id: GALLERY_TAB, label: "Gallery", mobileLabel:"Gallery", icon: ImageIcon, testid:"gallery-tab" },
            { id: STORE_TAB, label:"Shop & Affiliate", mobileLabel:"Shop", icon:ShoppingBag, testid:"store-tab" },
            { id: ESCORT_TAB, label: "Escort & Companion", mobileLabel:"Companion", icon: UserCircle, testid:"escort-tab" },
            { id: CAST_TAB, label: "Cast & Crew", mobileLabel:"Cast", icon: Users, testid:"cast-tab" },
            { id: null, label: "Home", icon: Home, isHome: true },
          ].map((item, index) => {
            const isActive = activeTab === item.id;
            const Icon = item.icon;
            
            if (item.isHome) {
              return (
                <Link
                  key="home"
                  to="/"
                  className="flex min-h-14 min-w-0 flex-col items-center justify-center gap-1 rounded-lg py-2 text-zinc-400 transition-colors hover:text-white"
                >
                  <Icon size={20} />
                  <span className="text-[10px] font-semibold">{item.label}</span>
                </Link>
              );
            }
            
            return (
              <button
                key={item.id}
                data-testid={item.testid}
                aria-label={item.label}
                aria-current={isActive?"page":undefined}
                onClick={() => {changeTab(item.id);setSidebarOpen(false);}}
                className={`flex min-h-14 min-w-0 flex-col items-center justify-center gap-1 rounded-lg px-0.5 py-2 transition-all duration-200 ${
                  isActive
                    ? "bg-gradient-to-r from-[#E11D48] to-[#FF10F0] text-white shadow-[0_0_15px_rgba(225,29,72,0.4)]"
                    : "text-zinc-400"
                }`}
              >
                <Icon size={20} />
                <span className="text-[9px] font-semibold sm:hidden">{item.mobileLabel}</span><span className="hidden text-[10px] font-semibold sm:block">{item.label}</span>
                {isActive && (
                  <div className="w-1 h-1 rounded-full bg-white animate-pulse" />
                )}
              </button>
            );
          })}
        </div>
      </nav>
    </div>
    {/* End Main Content Area */}

      {/* Gallery Viewer Modal */}
      {selectedGalleryItem && (
        <GalleryMediaViewer
          item={selectedGalleryItem}
          onClose={() => setSelectedGalleryItem(null)}
        />
      )}
    </div>
  );
}
